import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import type {
  ApplyRequestDto,
  ApplyResponseDto,
  BulkApproveResponseDto,
  BulkDecisionRequestDto,
  BulkLookupResponseDto,
  BulkRejectResponseDto,
  InsightActionPreviewDto,
  LookupOriginDto,
  OffsetPageDto,
  RevertItemResponseDto,
  RevertRunResponseDto,
  RunApprovalsDto,
  RunDetailDto,
  RunHistoryQuery,
  RunListRowDto,
} from '../models/insightAction.wire'

import { appHttp, unwrap, unwrapEnvelope } from '@/shared/http'

function requestConfig(signal?: AbortSignal, params?: Record<string, unknown>): AxiosRequestConfig {
  const config: AxiosRequestConfig = {
    suppressGlobalErrorHandler: true,
    meta: { suppressGlobalErrorHandler: true },
  }
  if (signal) config.signal = signal
  if (params) config.params = params
  return config
}

function cleanParams(query: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined))
}

export class InsightActionsApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  private insightBase(accountId: number, workspaceId: number, insightId: number): string {
    return `/accounts/${accountId}/workspaces/${workspaceId}/insights/${insightId}`
  }

  private runBase(accountId: number, workspaceId: number): string {
    return `/accounts/${accountId}/workspaces/${workspaceId}/insight-action-runs`
  }

  async preview(
    accountId: number,
    workspaceId: number,
    insightId: number,
    signal?: AbortSignal,
  ): Promise<InsightActionPreviewDto> {
    const response = await this.http.get(
      `${this.insightBase(accountId, workspaceId, insightId)}/actions/`,
      requestConfig(signal),
    )
    return unwrapEnvelope<InsightActionPreviewDto>(response)
  }

  async apply(
    accountId: number,
    workspaceId: number,
    insightId: number,
    body: ApplyRequestDto,
    signal?: AbortSignal,
  ): Promise<ApplyResponseDto> {
    const response = await this.http.post(
      `${this.insightBase(accountId, workspaceId, insightId)}/actions/apply/`,
      body,
      requestConfig(signal),
    )
    return unwrapEnvelope<ApplyResponseDto>(response)
  }

  async runHistory(
    accountId: number,
    workspaceId: number,
    insightId: number,
    query: RunHistoryQuery = {},
    signal?: AbortSignal,
  ): Promise<OffsetPageDto<RunListRowDto>> {
    const response = await this.http.get(
      `${this.insightBase(accountId, workspaceId, insightId)}/action-runs/`,
      requestConfig(signal, cleanParams({ ...query })),
    )
    return unwrap<OffsetPageDto<RunListRowDto>>(response)
  }

  async runDetail(
    accountId: number,
    workspaceId: number,
    runId: string,
    signal?: AbortSignal,
  ): Promise<RunDetailDto> {
    const response = await this.http.get(
      `${this.runBase(accountId, workspaceId)}/${runId}/`,
      requestConfig(signal),
    )
    return unwrapEnvelope<RunDetailDto>(response)
  }

  async lookupOrigin(
    accountId: number,
    workspaceId: number,
    platformActionRequest: string,
    signal?: AbortSignal,
  ): Promise<LookupOriginDto | null> {
    const response = await this.http.get(
      `${this.runBase(accountId, workspaceId)}/lookup/`,
      requestConfig(signal, { platform_action_request: platformActionRequest }),
    )
    return unwrapEnvelope<LookupOriginDto | null>(response)
  }

  async bulkLookupOrigin(
    accountId: number,
    workspaceId: number,
    requestIds: string[],
    signal?: AbortSignal,
  ): Promise<BulkLookupResponseDto> {
    const response = await this.http.post(
      `${this.runBase(accountId, workspaceId)}/lookup/`,
      { request_ids: requestIds },
      requestConfig(signal),
    )
    return unwrapEnvelope<BulkLookupResponseDto>(response)
  }

  async revertRun(
    accountId: number,
    workspaceId: number,
    runId: string,
    signal?: AbortSignal,
  ): Promise<RevertRunResponseDto> {
    const response = await this.http.post(
      `${this.runBase(accountId, workspaceId)}/${runId}/revert/`,
      {},
      requestConfig(signal),
    )
    return unwrapEnvelope<RevertRunResponseDto>(response)
  }

  async revertItem(
    accountId: number,
    workspaceId: number,
    runId: string,
    itemId: number,
    signal?: AbortSignal,
  ): Promise<RevertItemResponseDto> {
    const response = await this.http.post(
      `${this.runBase(accountId, workspaceId)}/${runId}/items/${itemId}/revert/`,
      {},
      requestConfig(signal),
    )
    return unwrapEnvelope<RevertItemResponseDto>(response)
  }

  async runApprovals(
    accountId: number,
    workspaceId: number,
    runId: string,
    signal?: AbortSignal,
  ): Promise<RunApprovalsDto> {
    const response = await this.http.get(
      `${this.runBase(accountId, workspaceId)}/${runId}/approvals/`,
      requestConfig(signal),
    )
    return unwrapEnvelope<RunApprovalsDto>(response)
  }

  async approveRun(
    accountId: number,
    workspaceId: number,
    runId: string,
    body: BulkDecisionRequestDto = {},
    signal?: AbortSignal,
  ): Promise<BulkApproveResponseDto> {
    const response = await this.http.post(
      `${this.runBase(accountId, workspaceId)}/${runId}/approve/`,
      cleanParams({ ...body }),
      requestConfig(signal),
    )
    return unwrapEnvelope<BulkApproveResponseDto>(response)
  }

  async rejectRun(
    accountId: number,
    workspaceId: number,
    runId: string,
    body: BulkDecisionRequestDto = {},
    signal?: AbortSignal,
  ): Promise<BulkRejectResponseDto> {
    const response = await this.http.post(
      `${this.runBase(accountId, workspaceId)}/${runId}/reject/`,
      cleanParams({ ...body }),
      requestConfig(signal),
    )
    return unwrapEnvelope<BulkRejectResponseDto>(response)
  }
}

export const insightActionsApi = new InsightActionsApi()
